import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid
} from '@mui/material';
import {
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
  School as SchoolIcon
} from '@mui/icons-material';

interface AccountTypeSelectionProps {
  onSelectType: (type: 'student' | 'parent' | 'teacher') => void;
}

const AccountTypeSelectionStep = ({ onSelectType }: AccountTypeSelectionProps) => {
  // Check if teacher registration is enabled (defaults to false)
  const isTeacherRegistrationEnabled = process.env.REACT_APP_ENABLE_TEACHER_REGISTRATION === 'true';

  // Calculate grid sizing based on whether teacher option is shown
  const gridSize = isTeacherRegistrationEnabled ? 4 : 6;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 }, my: { xs: 1, sm: 2 } }}>
      <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        I am registering as a:
      </Typography>

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        <Grid item xs={12} sm={12} md={gridSize}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, sm: 3, md: 4 },
              cursor: 'pointer',
              transition: 'all 0.3s',
              minHeight: { xs: 'auto', md: 240 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-4px)',
                borderColor: 'primary.main',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
              },
              border: '2px solid transparent'
            }}
            onClick={() => onSelectType('student')}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, sm: 2 } }}>
              <PersonIcon sx={{ fontSize: { xs: 50, sm: 60, md: 70 }, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Student
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.875rem', sm: '0.875rem' }, textAlign: 'center' }}>
              I want to participate in math competitions and track my progress
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={12} md={gridSize}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, sm: 3, md: 4 },
              cursor: 'pointer',
              transition: 'all 0.3s',
              minHeight: { xs: 'auto', md: 240 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-4px)',
                borderColor: 'secondary.main',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
              },
              border: '2px solid transparent'
            }}
            onClick={() => onSelectType('parent')}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, sm: 2 } }}>
              <SupervisorAccountIcon sx={{ fontSize: { xs: 50, sm: 60, md: 70 }, color: 'secondary.main' }} />
            </Box>
            <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Parent
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.875rem', sm: '0.875rem' }, textAlign: 'center' }}>
              I want to monitor my child's academic progress
            </Typography>
          </Paper>
        </Grid>

        {isTeacherRegistrationEnabled && (
          <Grid item xs={12} sm={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, sm: 3, md: 4 },
              cursor: 'pointer',
              transition: 'all 0.3s',
              minHeight: { xs: 'auto', md: 240 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-4px)',
                borderColor: 'success.main',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
              },
              border: '2px solid transparent'
            }}
            onClick={() => onSelectType('teacher')}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, sm: 2 } }}>
              <SchoolIcon sx={{ fontSize: { xs: 50, sm: 60, md: 70 }, color: 'success.main' }} />
            </Box>
            <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Teacher
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.875rem', sm: '0.875rem' }, textAlign: 'center' }}>
              I want to enroll and monitor my students' progress
            </Typography>
          </Paper>
        </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AccountTypeSelectionStep;
